import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { supabase } from '../config/supabase';

// Mirrors mobile/src/hooks/use-notifications-query.ts. The web build
// only ships notification *preferences* — no push delivery yet — but
// the prefs are still stored server-side so the toggles persist
// across devices and govern the mobile push experience.

export interface NotificationPreferences {
  readonly pushEnabled: boolean;
  readonly breakingNews: boolean;
  readonly criticalAlerts: boolean;
  readonly importantNotices: boolean;
}

export function useNotificationPreferencesQuery(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: async () => {
      const result = await apiClient.get<{ data: NotificationPreferences }>(
        '/notifications/preferences',
      );
      return result.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useUpdatePreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: Partial<NotificationPreferences>) => {
      const result = await apiClient.put<{ data: NotificationPreferences }>(
        '/notifications/preferences',
        preferences,
      );
      return result.data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.notifications.preferences(), data);
    },
  });
}

// ---------------------------------------------------------------------------
// Account deletion — DELETE /users/account, then sign out locally.
// Mirrors mobile/src/stores/auth-store.ts deleteAccount() exactly so
// the same DSR audit trail applies.
// ---------------------------------------------------------------------------

export function useDeleteAccountMutation() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/users/account');
      // Server cascade deleted everything — clear the local Supabase
      // session so the next page load lands on the marketing page.
      try {
        await supabase.auth.signOut();
      } catch {
        // Ignore — user no longer exists, signOut() may 404 against
        // Supabase. The local session is removed regardless.
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Password change — sends a reset email via Supabase. Mobile does the
// same: it's the only flow Supabase exposes from a logged-in client.
// ---------------------------------------------------------------------------

export async function sendPasswordResetEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/`,
  });
  if (error) throw error;
}
